import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Euro, ShoppingCart, Eye, Users, TrendingUp, Percent, Phone, Navigation, ArrowUp, ArrowDown, Minus, ShieldOff, Ban, Bot } from "lucide-react";
import { flagEmoji, fmtTime } from "@/lib/regie";
import { BOT_STYLES } from "@/pages/admin/SuperRegie";

const REASON_LABELS = { ip: "Blocage manuel d'IP", suspicious: "Chemins suspects (scanners / robots malveillants)" };
const reasonLabel = (code) => REASON_LABELS[code] || `${flagEmoji(code)} ${code}`;
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#D4AF37";
const INK = "#1A1918";
const COPPER = "#C08B5C";

const PRESETS = [
  { key: "7", label: "7 jours", days: 7 },
  { key: "30", label: "30 jours", days: 30 },
  { key: "90", label: "90 jours", days: 90 },
  { key: "custom", label: "Personnalisé" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function Delta({ current, previous }) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return (
    <span className="flex items-center gap-0.5 text-[11px] text-green-700"><ArrowUp className="h-3 w-3" /> nouveau</span>
  );
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return (
    <span className="flex items-center gap-0.5 text-[11px] text-ash"><Minus className="h-3 w-3" /> stable</span>
  );
  const up = pct > 0;
  return (
    <span className={`flex items-center gap-0.5 text-[11px] ${up ? "text-green-700" : "text-red-600"}`}>
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

const Stat = ({ icon: Icon, label, value, current, previous, testid }) => (
  <div data-testid={testid} className="bg-white rounded-sm border border-stone p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.16em] text-ash">{label}</p>
      <Icon className="h-5 w-5 text-copper" strokeWidth={1.5} />
    </div>
    <div className="flex items-baseline gap-2 mt-3">
      <p className="font-serif text-3xl text-ink">{value}</p>
      {previous !== undefined && <Delta current={current} previous={previous} />}
    </div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white border border-stone rounded-sm p-5">
    <h2 className="font-serif text-xl text-ink mb-4">{title}</h2>
    {children}
  </div>
);

export default function SuperStats() {
  const { user } = useAuth();
  const [preset, setPreset] = useState("30");
  const [customStart, setCustomStart] = useState(isoDaysAgo(30));
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const { start, end } = useMemo(() => {
    if (preset === "custom") return { start: customStart, end: customEnd };
    const p = PRESETS.find((x) => x.key === preset);
    return { start: isoDaysAgo(p.days - 1), end: todayIso() };
  }, [preset, customStart, customEnd]);

  useEffect(() => {
    setLoading(true);
    setErr(false);
    api.get("/superadmin/analytics", { params: { start, end } })
      .then(({ data }) => setD(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [start, end]);

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const periodLabel = d ? `${d.period.days} jour${d.period.days > 1 ? "s" : ""}` : "";
  // Fewer x-axis ticks on longer ranges so labels don't overlap.
  const tickInterval = d ? Math.max(0, Math.ceil(d.period.days / 8) - 1) : 4;

  return (
    <div data-testid="super-stats">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Super Admin</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Statistiques</h1>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex bg-white border border-stone rounded-sm overflow-hidden" data-testid="period-presets">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`period-preset-${p.key}`}
                onClick={() => setPreset(p.key)}
                className={`px-3.5 py-2 text-xs uppercase tracking-[0.1em] transition-colors ${
                  preset === p.key ? "bg-ink text-cream" : "text-graphite hover:bg-sand"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-2" data-testid="period-custom-inputs">
              <input
                type="date" data-testid="period-start-input" value={customStart} max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-stone bg-white text-sm px-2.5 py-1.5 rounded-sm"
              />
              <span className="text-ash text-sm">→</span>
              <input
                type="date" data-testid="period-end-input" value={customEnd} min={customStart} max={todayIso()}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-stone bg-white text-sm px-2.5 py-1.5 rounded-sm"
              />
            </div>
          )}
        </div>
      </div>

      {err && (
        <p className="text-destructive mb-4">Erreur de chargement des statistiques pour cette période.</p>
      )}

      {loading || !d ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat testid="stat-ca" icon={Euro} label="Chiffre d'affaires" value={eur(d.revenue)} current={d.revenue} previous={d.prev_revenue} />
            <Stat testid="stat-orders" icon={ShoppingCart} label="Commandes" value={d.orders} current={d.orders} previous={d.prev_orders} />
            <Stat testid="stat-avg" icon={TrendingUp} label="Panier moyen" value={eur(d.avg_basket)} current={d.avg_basket} previous={d.prev_avg_basket} />
            <Stat testid="stat-views" icon={Eye} label="Pages vues" value={d.total_views} current={d.total_views} previous={d.prev_total_views} />
            <Stat testid="stat-visitors" icon={Users} label="Visiteurs uniques" value={d.unique_visitors} current={d.unique_visitors} previous={d.prev_unique_visitors} />
            <Stat testid="stat-conversion" icon={Percent} label="Taux de conversion" value={`${d.conversion}%`} current={d.conversion} previous={d.prev_conversion} />
            <Stat testid="stat-store-calls" icon={Phone} label="Appels magasins" value={d.store_call_clicks} current={d.store_call_clicks} previous={d.prev_store_call_clicks} />
            <Stat testid="stat-store-directions" icon={Navigation} label="Itinéraires demandés" value={d.store_directions_clicks} current={d.store_directions_clicks} previous={d.prev_store_directions_clicks} />
            <Stat testid="stat-blocked-requests" icon={ShieldOff} label="Requêtes refusées (blocage)" value={d.blocked_requests ?? 0} current={d.blocked_requests ?? 0} previous={d.prev_blocked_requests ?? 0} />
            <Stat testid="stat-blocked-ips" icon={Ban} label="IP bloquées actives" value={d.blocked_ips ?? 0} current={d.blocked_ips ?? 0} previous={d.prev_blocked_ips ?? 0} />
            <Stat testid="stat-bot-views" icon={Bot} label="Visites robots (exclues)" value={d.bot_views ?? 0} current={d.bot_views ?? 0} previous={d.prev_bot_views ?? 0} />
          </div>
          <p className="text-xs text-ash mt-2">Pages vues et visiteurs uniques ne comptent que les humains : Google/Bing vérifiés, imposteurs et autres robots sont comptés à part.</p>

          {d.bots_breakdown?.length > 0 && (
            <div className="mt-4" data-testid="bots-breakdown">
              <Card title="Robots détectés sur la période">
                <div className="space-y-2">
                  {d.bots_breakdown.map((b, i) => {
                    const conf = BOT_STYLES[b.bot_type] || BOT_STYLES.bot;
                    return (
                      <div key={`${b.bot_type}-${b.bot_name}-${i}`} className="flex items-center gap-3 text-sm">
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${conf.cls}`}>{conf.label(b.bot_name)}</span>
                        <span className="flex-1 text-ash text-xs">{b.sessions} session{b.sessions > 1 ? "s" : ""}</span>
                        <span className="font-mono text-ink w-16 text-right">{b.views} vues</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-ash mt-3">Vérifié = IP dans les plages officielles du moteur. Faux robot = se prétend Google/Bing depuis une IP étrangère au moteur : à bloquer.</p>
              </Card>
            </div>
          )}

          {(d.top_blocked_ips?.length > 0 || d.blocked_by_country?.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-4 mt-4" data-testid="blocked-breakdown">
              <Card title="Visiteurs bloqués — top IP refusées">
                <div className="space-y-2">
                  {d.top_blocked_ips.map((b, i) => (
                    <div key={b.ip} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-ash font-mono">{i + 1}</span>
                      <span className="font-mono text-ink">{b.ip}</span>
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200">{b.reason === "ip" ? "IP" : b.reason === "suspicious" ? "suspect" : b.reason === "session" ? "appareil" : `pays ${b.country_code || ""}`}</span>
                      {b.bot_type && (() => { const c = BOT_STYLES[b.bot_type] || BOT_STYLES.bot; return <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${c.cls}`}>{c.label(b.bot_name)}</span>; })()}
                      <span className="flex-1 text-ash text-xs text-right">{b.last_at ? fmtTime(b.last_at) : ""}</span>
                      <span className="font-mono text-red-600 w-12 text-right">{b.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title="Requêtes refusées par origine">
                <div className="space-y-2">
                  {d.blocked_by_country.map((b) => (
                    <div key={b.country_code} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 text-ink">{reasonLabel(b.country_code)}</span>
                      <span className="font-mono text-red-600">{b.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
          <p className="text-xs text-ash mt-3">Variation vs les {periodLabel} précédents.</p>

          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <Card title={`Chiffre d'affaires — ${periodLabel}`}>
              {d.revenue_series.every((r) => r.revenue === 0) ? (
                <p className="text-sm text-ash">Aucune vente sur cette période.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={d.revenue_series} margin={{ left: -18, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE5DC" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8782" }} tickFormatter={(x) => x.slice(8)} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#8A8782" }} />
                    <Tooltip formatter={(v) => eur(v)} labelStyle={{ color: INK }} contentStyle={{ borderRadius: 2, border: "1px solid #EAE5DC", fontSize: 12 }} />
                    <Area type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title={`Consultations — ${periodLabel}`}>
              {d.views_series.every((v) => v.views === 0) ? (
                <p className="text-sm text-ash">Aucune consultation sur cette période.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={d.views_series} margin={{ left: -18, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="vw" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COPPER} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={COPPER} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE5DC" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8782" }} tickFormatter={(x) => x.slice(8)} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#8A8782" }} allowDecimals={false} />
                    <Tooltip labelStyle={{ color: INK }} contentStyle={{ borderRadius: 2, border: "1px solid #EAE5DC", fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" stroke={COPPER} strokeWidth={2} fill="url(#vw)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <Card title="Produits les plus vendus">
              {d.top_products.length === 0 ? <p className="text-sm text-ash">Aucune vente sur cette période.</p> : (
                <div className="space-y-2">
                  {d.top_products.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-ash font-mono">{i + 1}</span>
                      <span className="flex-1 text-ink truncate">{p.name}</span>
                      <span className="text-ash">{p.qty} vendus</span>
                      <span className="font-mono text-ink w-20 text-right">{eur(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Catégories les plus performantes">
              {d.top_categories.length === 0 ? <p className="text-sm text-ash">Aucune donnée sur cette période.</p> : (
                <ResponsiveContainer width="100%" height={Math.max(160, d.top_categories.length * 44)}>
                  <BarChart data={d.top_categories} layout="vertical" margin={{ left: 30, right: 16 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: INK }} width={90} />
                    <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 2, border: "1px solid #EAE5DC", fontSize: 12 }} />
                    <Bar dataKey="revenue" radius={[0, 2, 2, 0]}>
                      {d.top_categories.map((_, i) => <Cell key={i} fill={i % 2 ? COPPER : GOLD} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <div className="mt-4">
            <Card title="Produits les plus consultés">
              {d.most_viewed_products.length === 0 ? <p className="text-sm text-ash">Aucune consultation enregistrée sur cette période.</p> : (
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  {d.most_viewed_products.map((p, i) => (
                    <div key={p.slug} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-ash font-mono">{i + 1}</span>
                      <span className="flex-1 text-ink truncate">{p.name}</span>
                      <span className="flex items-center gap-1 text-ash"><Eye className="h-3.5 w-3.5" /> {p.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
